/**
 * Single source of truth for reading a human-facing message out of the backend's
 * error envelopes. Handles the legacy {errorInformation, generalProcessingStatus}
 * shape, the {errors, status} shape, and the NineBusinessRs shape used by the
 * Support Agent API ({status: {status, statusMessage}, errors: [{errorCode,
 * errorMessage}]}) — see services/apiServices/SupportApiService.ts. Shared by
 * every API service so none of them drift apart on error copy.
 *
 * Purely additive: every branch below only adds a new field name to check: no
 * existing lookup was removed or reordered ahead of one that used to run first.
 */
export function extractApiErrorMessage(data: any): string | null {
  if (!data) return null;
  return (
    data?.errors?.[0]?.errorDescription ??
    data?.errors?.[0]?.errorMessage ?? // NineBusinessRs / Support Agent shape
    data?.errors?.[0]?.errorDetail ?? // bulkImageAnalyze's ErrorInformation shape
    data?.errorInformation?.[0]?.errorDetail ??
    data?.errorInformation?.[0]?.errorMessage ?? // doc-shaped errorInformation
    data?.generalProcessingStatus?.processingDetails ??
    (typeof data?.error === "string" ? data.error : null) ?? // bulkImageAnalyze total-failure envelope
    data?.status?.statusMessage ?? // NineBusinessRs object-shaped status
    (typeof data?.status === "string" ? data.status : null) // guarded legacy fallback
  );
}

/**
 * Runs a request, converting fetch's raw network rejection (TypeError:
 * "Failed to fetch" on connection/CORS errors) into copy an end user can act
 * on. HTTP error statuses are NOT handled here — callers keep owning those.
 */
export async function fetchOrThrowFriendly(
  request: () => Promise<Response>
): Promise<Response> {
  try {
    return await request();
  } catch {
    throw new Error("Couldn't reach the server. Please check your connection and try again.");
  }
}

/**
 * Call right after a request, before response.json(). A non-2xx response
 * (most importantly a bare-metal 502/503/504 from the gateway/load-balancer,
 * which carries no usable JSON body — often HTML or nothing at all) throws a
 * friendly Error instead of letting callers crash trying to parse it or read
 * into an unexpected shape. That thrown error becomes the thunk's `rejected`
 * action automatically, which every slice already turns into `errorMap.*` —
 * so this one guard is enough to stop a gateway outage from being silently
 * read as "zero results" further downstream.
 */
export async function throwIfNotOk(
  response: Response,
  fallbackMessage = "Something went wrong. Please try again."
): Promise<void> {
  if (response.ok) return;
  if (response.status === 502 || response.status === 503 || response.status === 504) {
    throw new Error("The service is temporarily unavailable. Please try again in a moment.");
  }
  const data = await response.json().catch(() => null);
  throw new Error(extractApiErrorMessage(data) ?? fallbackMessage);
}

/**
 * Call on an ALREADY-PARSED body, after `response.json()`, for endpoints whose
 * controller can return a real failure with HTTP 200 -- createProduct and
 * updateProduct do this today: their `catch (IllegalArgumentException)`
 * branches return a bare NineBusinessRs object rather than a ResponseEntity
 * with a real status, so Spring defaults the response to 200 even though
 * `errorInformation` is populated and `contents` is null. `throwIfNotOk`
 * can't catch this -- it only looks at the HTTP status, which is 200 here --
 * so this checks the body's own error signal instead, regardless of status.
 *
 * Success for these two endpoints specifically is `errorInformation`
 * absent/empty -- verified against every response constructed in
 * ProductController's createProduct/updateProduct methods (and the
 * NineExceptionHandler branches an uncaught exception from either falls
 * into): none of them populate `errorInformation` alongside a non-null
 * `contents`, so there is no "success with warnings" shape to worry about
 * HERE. That does NOT generalize to every endpoint using this envelope --
 * bulkCreateProduct, for one, deliberately returns partial success this way
 * (some items in `contents`, failures in `errorInformation`), which is
 * exactly why this helper is only wired into createProduct/updateProduct
 * and not bulkCreateProducts. Re-verify against the controller before
 * reusing this for another endpoint. Also does NOT check
 * `generalProcessingStatus`, which is null on both success AND failure for
 * this envelope shape.
 */
export function throwIfErrorInBody(
  data: any,
  fallbackMessage = "Something went wrong. Please try again."
): void {
  const errorInfo = data?.errorInformation;
  if (Array.isArray(errorInfo) && errorInfo.length > 0) {
    throw new Error(extractApiErrorMessage(data) ?? fallbackMessage);
  }
}

/** Toast text for a caught `dispatch(thunk).unwrap()` rejection. Thunks here
 *  reject with an already-friendly string; anything else (a thrown Error, an
 *  unexpected value) falls back to the caller's generic copy. */
export function rejectionMessage(error: unknown, fallback: string): string {
  if (typeof error === "string") return error;
  return error instanceof Error && error.message ? error.message : fallback;
}

export default {
  extractApiErrorMessage,
  fetchOrThrowFriendly,
  throwIfNotOk,
  throwIfErrorInBody,
  rejectionMessage,
};
